import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router";
import { overviewDecision, readProject } from "../../api/projectsApi";
import { readProjectSettings } from "../../api/settingsApi";
import { appPaths } from "../../shared/routing/appPaths";
import { useLoad } from "../../shared/loading/useLoad";
import { useTabTitle } from "../../shell/workspace";
import LoadStatus from "../../ui/feedback/LoadStatus";
import PageNav from "../../ui/navigation/PageNav";
import { useProjects } from "../projectContext";
import { useOpenedProject } from "../useOpenedProject";
import type { ProjectSettingsContext } from "./projectSettingsContext";

/**
 * `/projects/<id>/settings/…` (web-app.md WA-5, redesign R3-3): one project's settings as
 * tabs: Access (its groups and people), Rules, History and General. Opening it makes its
 * project the chosen one, so the project menu names the project whose settings show.
 */
export default function ProjectSettingsLayout() {
  const { t } = useTranslation();
  const { projectId = "" } = useParams();
  const loadProject = useCallback((signal: AbortSignal) => readProject(projectId, signal), [projectId]);
  const { state: project, reload: reloadProject, refresh: refreshProject } = useLoad(loadProject);
  const { refreshSelectedProject } = useProjects();
  const loadSettings = useCallback((signal: AbortSignal) => readProjectSettings(projectId, signal), [projectId]);
  const { state: settings, reload: reloadSettings, refresh: refreshSettings } = useLoad(loadSettings);

  // Only a project the user may see, so a mistyped address doesn't replace the choice.
  useOpenedProject(project.status === "loaded" ? project.value.project.projectId : null);
  // The tab says whose settings show, as the page's heading does (IM-SHELL, C6).
  const heading = project.status === "loaded" ? t("projects.settings.title", { project: project.value.project.name }) : null;
  useTabTitle(heading === null ? null : { kind: "other", name: heading });

  const permissionsChanged = useCallback(() => {
    refreshProject();
    refreshSelectedProject();
  }, [refreshProject, refreshSelectedProject]);

  if (project.status !== "loaded") return <LoadStatus state={project} loadingLabel={t("common.loading")} onRetry={reloadProject} />;
  if (settings.status !== "loaded") return <LoadStatus state={settings} loadingLabel={t("common.loading")} onRetry={reloadSettings} />;
  const summary = project.value.project;
  const context: ProjectSettingsContext = {
    overview: project.value,
    settings: settings.value,
    permissionsChanged,
    settingsChanged: () => { refreshSettings(); permissionsChanged(); },
  };
  return (
    <div className="max-w-6xl space-y-5 p-4">
      <h2 className="app-title text-xl font-bold">{heading}</h2>
      <PageNav
        label={t("projects.settings.tabs.label")}
        pages={[
          { path: appPaths.projectSettingsAccess(summary.projectId), label: t("projects.settings.tabs.access") },
          { path: appPaths.projectSettingsRules(summary.projectId), label: t("projects.settings.tabs.rules") },
          { path: appPaths.projectSettingsHistory(summary.projectId), label: t("projects.settings.tabs.history"), requires: overviewDecision(project.value, "manage_members") },
          { path: appPaths.projectSettingsGeneral(summary.projectId), label: t("projects.settings.tabs.general") },
        ]}
      />
      {/* One key for every tab: another project starts them all afresh. */}
      <Fragment key={summary.projectId}>
        <Outlet context={context} />
      </Fragment>
    </div>
  );
}
